import { PlusOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Archive } from '@styled-icons/bootstrap/Archive';
import { useLocation, useNavigate } from '@tanstack/react-location';
import { Button, message, Modal, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { StyledIconButton } from '@/components/Buttons';
import { useArchiveConversationMutation } from '@/generated/graphql';
import useUser from '@/hooks/useUser';

interface Props {
  id: string;
  archived: boolean;
  iconOnly?: boolean;
  answered?: boolean;
}

function ArchiveConversation({
  id,
  archived,
  iconOnly,
  answered,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const tenantArchiveMessage = `${t('Archive')}/${t('turnDown')}`;
  const user = useUser();
  const isTenant = user?.activeClient?.type === 'tenant';

  // Show "Arkivera/Tacka nej" only if tenant hasn't answered yet
  const showDeclineOption = isTenant && !answered;

  const navigate = useNavigate();
  const path = useLocation();

  const apollo = useApolloClient();

  const [archiveConversation, { loading }] = useArchiveConversationMutation({
    variables: {
      id,
      archived: !archived,
    },
    onCompleted: async res => {
      res.setArchived.archived
        ? message.success(t('Conversation archived'))
        : message.success(t('Sent to inbox'));

      await apollo.reFetchObservableQueries();

      if (archived) {
        navigate({
          to: path.current.pathname.replace(/\/\w+$/, ''),
        });
      }
    },
    onError: () => {
      message.error(t('error'));
    },
  });

  const withText = !iconOnly;

  if (archived) {
    return (
      <Tooltip title={t('Add to inbox')} open={withText ? false : undefined}>
        <Button
          loading={loading}
          type={'text'}
          icon={<PlusOutlined />}
          onClick={() => archiveConversation()}
          style={withText ? undefined : { width: '40px' }}
        >
          {withText ? t('Add to inbox') : ''}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={showDeclineOption ? tenantArchiveMessage : t('Archive')}
      open={withText ? false : undefined}
    >
      <StyledIconButton
        loading={loading}
        type={'text'}
        $withText={withText}
        icon={<Archive size={16} />}
        onClick={() => archiveConversation()}
        style={withText ? undefined : { width: '40px' }}
      >
        {withText
          ? showDeclineOption
            ? tenantArchiveMessage
            : t('Archive')
          : ''}
      </StyledIconButton>
    </Tooltip>
  );
}

export default ArchiveConversation;
